import React, { useContext, useState, useEffect } from "react";
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import ButtonPrimary from "../buttons/ButtonPrimary";
import {
  getAddress,
  AddressPurpose,
  BitcoinNetworkType,
  BitcoinProvider,
  getDefaultProvider,
} from "sats-connect";
import { ConnectionStatusContext } from "../../provider/ConnectionStatusProvider";
import { useNavigate } from "react-router-dom";
import MagicedenWallet from "../../wallets/MagicedenWallet";
import AlertModal from "./AlertModal";
import UniSatNotInstalled from "./UniSatNotInstalled";

// Dummy wallet connection functions (replace with actual logic)
const connectUniSatWallet = async () => {
  // Logic to connect UniSat Wallet
  return "UniSat Wallet Address";
};

const connectXverseWallet = async () => {
  // Logic to connect Xverse Wallet
  return "Xverse Wallet Address";
};

const connectMagicEdenWallet = async () => {
  // Logic to connect Magic Eden Wallet
  return "Magic Eden Wallet Address";
};

const WalletModal = ({ title }: { title: string }) => {
  const [isUniSatInstalled, setIsUniSatInstalled] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [connectedWallet, setConnectedWallet] = useState<string | null>(null);
  const connectionStatus = useContext(ConnectionStatusContext);
  const [balances, setBalances] = useState<Record<string, number>>({});
  const [ordinals, setOrdinals] = useState<Record<string, any[]>>({});
  const [addresses, setAddresses] = useState<string[]>([]);
  const [individualCollections, setIndividualCollections] = useState<any[]>([]);
  const [collectionSummary, setCollectionSummary] = useState<any[]>([]);
  const [showAlertModal, setShowAlertModal] = useState<any>(false);
  const [showUniSatNotInstalledModal, setShowUniSatNotInstalledModal] =
    useState<any>(false);

  const fetchBalance = async (address: string) => {
    try {
      const response = await fetch(
        `https://api.blockcypher.com/v1/btc/main/addrs/${address}/balance`
      );
      const data = await response.json();
      return data.balance;
    } catch (err) {
      console.error("Error fetching balance:", err);
      return 0;
    }
  };

  // Define fetchOrdinalsNFTs function
  const fetchOrdinalsNFTs = async (address: string) => {
    // Placeholder logic, replace with actual API call to fetch Ordinals NFTs
    return [];
  };

  // const fetchNFTData = async () => {
  //   const oneDayAgo = new Date(new Date().getTime() - 24 * 60 * 60 * 1000);
  //   const delay = (ms: number) =>
  //     new Promise((resolve) => setTimeout(resolve, ms));

  //   let totalCollections: { [key: string]: number } = {};
  //   let newInLast24h: { [key: string]: number } = {};

  //   const fetchAddressData = async (address: string) => {
  //     const url = `https://api.allorigins.win/raw?url=${encodeURIComponent(
  //       "https://api-mainnet.magiceden.io/v2/ord/btc/wallets/collections?ownerAddress="
  //     )}${address}`;

  //     try {
  //       await delay(2000);
  //       const response = await fetch(url);
  //       const data: CollectionData[] = await response.json();

  //       let collectionsInWallet: { [key: string]: CollectionInfo } = {};

  //       data.forEach((collection) => {
  //         const createdAt = new Date(collection.createdAt);
  //         const collectionName = collection.name;

  //         totalCollections[collectionName] =
  //           (totalCollections[collectionName] || 0) + 1;
  //         if (createdAt > oneDayAgo) {
  //           newInLast24h[collectionName] =
  //             (newInLast24h[collectionName] || 0) + 1;
  //         }

  //         collectionsInWallet[collectionName] = collectionsInWallet[
  //           collectionName
  //         ] || {
  //           image: collection.imageURI,
  //           description: collection.description,
  //           twitterLink: collection.twitterLink,
  //           websiteLink: collection.websiteLink,
  //           count: 0,
  //         };
  //         collectionsInWallet[collectionName].count++;
  //       });

  //       return {
  //         address,
  //         collectionsInWallet,
  //       };
  //     } catch (error) {
  //       console.error("Fetch error:", error);
  //       return {
  //         address,
  //         collectionsInWallet: {},
  //       };
  //     }
  //   };

  //   const results = await Promise.all(addresses.map(fetchAddressData));

  //   const individualData: any[] = [];
  //   results.forEach((result) => {
  //     Object.entries(result.collectionsInWallet).forEach(([name, info]) => {
  //       individualData.push({
  //         address: result.address,
  //         ...info,
  //         name,
  //       });
  //     });
  //   });

  //   setIndividualCollections(individualData);

  //   const sortedEntries = Object.entries(totalCollections).sort((a, b) => {
  //     const newA = newInLast24h[a[0]] || 0;
  //     const newB = newInLast24h[b[0]] || 0;
  //     if (newA > newB) return -1;
  //     if (newA < newB) return 1;
  //     return b[1] - a[1];
  //   });

  //   setCollectionSummary(
  //     sortedEntries.map(([name, count]) => ({
  //       name,
  //       count,
  //       newInLast24h: newInLast24h[name] || 0,
  //     }))
  //   );
  // };

  const connectWallet = async () => {
    try {
      connectionStatus?.setLoading(true);
      const provider: any = await getDefaultProvider();

      await getAddress({
        getProvider: async () => provider,
        payload: {
          purposes: [AddressPurpose.Ordinals, AddressPurpose.Payment],
          message: "Address for receiving Ordinals and payments",
          network: {
            type: BitcoinNetworkType.Mainnet,
          },
        },
        onFinish: async (response) => {
          const accounts = response.addresses as unknown as any[];
          connectionStatus?.setType("xverse");
          connectionStatus?.setAccounts(accounts);

          const newBalances: Record<string, number> = {};
          const newOrdinals: Record<string, any[]> = {};
          const addressList: string[] = accounts.map(
            (account) => account.address
          );

          setAddresses(addressList);

          for (const account of accounts) {
            newBalances[account.address] = await fetchBalance(account.address);
            newOrdinals[account.address] = await fetchOrdinalsNFTs(
              account.address
            );

            console.log(
              `Balance for ${account.address}: ${
                newBalances[account.address]
              } satoshis`
            );
            console.log(
              `Ordinals for ${account.address}:`,
              newOrdinals[account.address]
            );
          }
          connectionStatus?.setBalance(newBalances);
          const localStorageItems = {
            accounts: accounts,
            balance: newBalances,
            connected: true,
            type: "xverse",
          };

          localStorage.setItem("auth-item", JSON.stringify(localStorageItems));

          setBalances(newBalances);
          setOrdinals(newOrdinals);
          connectionStatus?.setLoading(false);
          navigate("/dashboard");
        },
        onCancel: () => {
          // alert("Request canceled");
          setShowAlertModal(true);
          connectionStatus?.setLoading(false);
        },
      });
    } catch (err) {
      console.error("Error connecting wallet:", err);
      connectionStatus?.setAccounts([]);
      connectionStatus?.setLoading(false);
    }
  };

  useEffect(() => {
    if (typeof window.unisat !== "undefined") {
      setIsUniSatInstalled(true);
      console.log("UniSat Wallet is installed!");
    } else {
      console.log("UniSat Wallet is not installed.");
    }
  }, []);

  const connectUnisat = async () => {
    if (!isUniSatInstalled) {
      // alert("UniSat Wallet is not installed.");
      setShowUniSatNotInstalledModal(true);
      return;
    }

    // setConnecting(true);

    try {
      connectionStatus?.setLoading(true);
      const accounts = await window.unisat.requestAccounts();
      connectionStatus?.setAccounts(accounts[0]);
      console.log("accounts", accounts);
      // setAccount(accounts[0]);

      const balance = await window.unisat.getBalance();
      connectionStatus?.setBalance(balance?.total);
      connectionStatus?.setType("unisat");
      console.log("balance", balance);
      navigate("/dashboard");
      // const bitcoinAddr = await window.unisat.getBitcoinAddress();
      // console.log("bitcoinAddr", bitcoinAddr);
      // setBitcoinAddress(bitcoinAddr);
      const localStorageItems = {
        accounts: accounts[0],
        balance: balance,
        connected: true,
        type: "unisat",
      };

      localStorage.setItem("auth-item", JSON.stringify(localStorageItems));
      connectionStatus?.setLoading(false);
    } catch (error) {
      connectionStatus?.setLoading(false);
      console.error("Failed to connect to UniSat Wallet:", error);
      // alert("Failed to connect to UniSat Wallet.");
      setShowUniSatNotInstalledModal(true);
    } finally {
      // setConnecting(false);
    }
  };

  const handleConnectWallet = async (walletType: string) => {
    let walletAddress = null;
    try {
      if (walletType === "UniSat") {
        // walletAddress = await connectUniSatWallet();
        connectUnisat();
      } else if (walletType === "xverse") {
        connectWallet();
        // connectWallet2();
      } else if (walletType === "MagicEden") {
        // connectWallet2();
      }
      setConnectedWallet(walletAddress);
      setOpen(false);
    } catch (error) {
      console.error("Failed to connect wallet:", error);
    }
  };

  // console.log("ConnectionStatusContext", connectionStatus?.accounts);

  return (
    <>
      {showAlertModal && <AlertModal text="Request canceled" />}
      {showUniSatNotInstalledModal && <UniSatNotInstalled />}
      <ButtonPrimary
        type="button"
        onClick={() => setOpen(true)}
        title={title}
      />

      <Dialog open={open} onClose={setOpen} className="relative z-30">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-gray-900 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 w-full sm:max-w-sm data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="absolute top-3 right-2.5 text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center dark:hover:bg-gray-800 dark:hover:text-white"
              >
                <XMarkIcon className="w-5 h-5" />
                <span className="sr-only">Close modal</span>
              </button>

              <div className="p-4 border-b rounded-t border-gray-600">
                <h3 className="text-base font-semibold text-white">
                  Connect wallet
                </h3>
              </div>

              <div className="p-4">
                <ul className="my-4 space-y-3">
                  <li>
                    <button
                      type="button"
                      onClick={() => handleConnectWallet("UniSat")}
                      className="w-full text-left flex items-center p-3 text-base font-bold text-gray-300 rounded-lg bg-gray-800 hover:bg-gray-600 group hover:shadow dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
                    >
                      <img
                        className="h-8 w-8 object-contain"
                        alt="logo of UniSat Wallet"
                        src="/assets/images/w-icon-1.svg"
                      />
                      <span className="flex-1 ml-3 whitespace-nowrap">
                        UniSat Wallet
                      </span>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      // onClick={connectWallet}
                      onClick={() => handleConnectWallet("xverse")}
                      className="w-full text-left flex items-center p-3 text-base font-bold text-gray-300 rounded-lg bg-gray-800 hover:bg-gray-600 group hover:shadow dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
                    >
                      <img
                        className="h-8 w-8 object-contain"
                        alt="logo of Xverse Wallet"
                        src="/assets/images/w-icon-2.svg"
                      />
                      <span className="flex-1 ml-3 whitespace-nowrap">
                        Xverse Wallet
                      </span>
                    </button>
                    {/* <XverseWallet /> */}
                  </li>
                  <li>
                    {/* <button
                      type="button"
                      onClick={() => handleConnectWallet("MagicEden")}
                      className="w-full text-left flex items-center p-3 text-base font-bold text-gray-300 rounded-lg bg-gray-800 hover:bg-gray-600 group hover:shadow dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-white"
                    >
                      <img
                        className="h-8 w-8 object-contain"
                        alt="logo of Magic Eden"
                        src="/assets/images/w-icon-3.png"
                      />
                      <span className="flex-1 ml-3 whitespace-nowrap">
                        Magic Eden
                      </span>
                    </button> */}
                    <MagicedenWallet />
                  </li>
                </ul>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      {connectedWallet && (
        <div className="mt-4 text-white">
          <p>Connected Wallet: {connectedWallet}</p>
        </div>
      )}
    </>
  );
};

export default WalletModal;
